import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

const posts = [
  {
    slug: "best-places-to-visit-in-pondicherry",
    title: "Best Places to Visit in Pondicherry",
    excerpt: "Discover the best places to visit in Pondicherry — French Quarter, Auroville, Paradise Beach, temples, and more. Includes Pondicherry sightseeing taxi tips for each attraction.",
    date: "March 15, 2026",
    readTime: "8 min read",
    content: `Pondicherry, the charming coastal union territory of India, offers a unique blend of French colonial heritage and Tamil culture. Whether you're a history buff, beach lover, spiritual seeker, or food enthusiast, Pondicherry has something magical for everyone. Here's our comprehensive guide to the best places to visit in Pondicherry.

**1. Promenade Beach (Rock Beach)**
The iconic Promenade Beach, stretching 1.5 km along the Bay of Bengal, is the heart of Pondicherry. Lined with heritage buildings, cafés, and the Gandhi statue, it's perfect for morning walks and evening strolls. The beach is pedestrian-only from 6 PM onwards, making it a peaceful retreat from the city's bustle.

**2. French Quarter (White Town)**
Walk through the cobblestoned streets of the French Quarter, where mustard-yellow colonial buildings, bougainvillea-draped walls, and charming cafés transport you to a little corner of France. Rue Romain Rolland, Rue Suffren, and Rue Dumas are must-explore streets. Our taxi service can drop you at the heart of White Town for a leisurely exploration.

**3. Auroville (City of Dawn)**
Located 12 km from Pondicherry, Auroville is an international township dedicated to human unity. The Matrimandir, a golden spherical meditation hall, is its centerpiece. Visitors can explore the visitor center, boutiques, and organic cafés. Book our Pondicherry taxi for a comfortable half-day trip to Auroville.

**4. Paradise Beach**
Accessible only by boat from Chunnambar Boat House, Paradise Beach is a pristine, secluded stretch of golden sand. The 20-minute boat ride through backwaters is an experience in itself. Perfect for swimming, sunbathing, and picnics away from the crowds.

**5. Sri Aurobindo Ashram**
Founded in 1926, this spiritual community in the heart of Pondicherry attracts seekers from around the world. The ashram's main building houses the samadhi (final resting place) of Sri Aurobindo and The Mother. Visitors experience profound peace in this spiritual haven.

**6. Basilica of the Sacred Heart of Jesus**
This stunning Gothic church, built in the early 20th century, features beautiful stained glass windows depicting events from the life of Christ. One of the finest examples of Gothic architecture in South India, it's a must-visit for architecture enthusiasts.

**7. Serenity Beach**
True to its name, Serenity Beach offers a calm and peaceful atmosphere, popular with surfers and yoga practitioners. The golden sand beach with rocky formations is less crowded than Promenade Beach and perfect for those seeking tranquility.

**Getting Around Pondicherry**
The best way to explore all these attractions is with a reliable taxi service. Our Pondicherry Taxi offers flexible local tour packages that cover all major attractions. With an experienced local driver who doubles as your guide, you won't miss any hidden gems. Book your Pondicherry sightseeing taxi today!`,
  },
  {
    slug: "pondicherry-travel-guide",
    title: "Pondicherry Travel Guide 2026",
    excerpt: "Complete Pondicherry travel guide 2026 — best time to visit, how to reach by taxi from Chennai, where to stay, what to eat, and essential Puducherry travel tips.",
    date: "March 10, 2026",
    readTime: "10 min read",
    content: `Planning a trip to Pondicherry? This comprehensive travel guide covers everything from the best time to visit to getting around, accommodation options, food recommendations, and insider tips that will make your Puducherry vacation truly memorable.

**Best Time to Visit Pondicherry**
The ideal time to visit Pondicherry is from October to March when the weather is pleasant with temperatures between 20°C and 32°C. The monsoon season (October-December) brings occasional rain but also lush greenery. Avoid the peak summer months of April-June when temperatures can exceed 40°C.

**How to Reach Pondicherry**
By Air: The nearest major airport is Chennai International Airport (MAA), 150 km away. Our Pondicherry Taxi provides reliable airport transfer service from Chennai to Pondicherry. Pondicherry also has a small airport with limited flights.

By Train: Pondicherry Junction (PDY) is connected to Chennai, Villupuram, and other cities. However, train frequency is limited, making taxi or bus a more convenient option for most travelers.

By Road: Pondicherry is well-connected by road. The scenic East Coast Road (ECR) from Chennai is the most popular route. Our taxi service offers comfortable rides from Chennai, Bangalore, and other major cities.

**Where to Stay in Pondicherry**
The French Quarter (White Town) is the most popular area for tourists, with heritage hotels and boutique stays. For budget options, look at areas near MG Road and Jawaharlal Nehru Street. For beach lovers, properties near Serenity Beach and Auroville offer peaceful retreats.

**Must-Try Food in Pondicherry**
Pondicherry's food scene is a delightful fusion of French and Tamil cuisines. Don't miss: French crepes and croissants at Baker Street, authentic South Indian thali at Surguru, seafood at Le Club, and filter coffee at Indian Coffee House. The Sunday brunch culture at White Town cafés is a quintessential Pondicherry experience.

**Getting Around Pondicherry**
While the French Quarter is walkable, you'll need transportation for Auroville, beaches, and temples. Renting a scooter is popular but can be risky for unfamiliar riders. The safest and most comfortable option is booking a taxi. Our Pondicherry Taxi service offers hourly rentals perfect for sightseeing at your own pace.

**Essential Tips**
- Carry sunscreen and a hat, especially during summer
- Most attractions are free or have minimal entry fees
- French Quarter is best explored on foot or by cycle
- Book accommodation in advance during peak season (December-January)
- Respect the dress code at religious sites
- Our taxi drivers can recommend the best local experiences — just ask!`,
  },
  {
    slug: "chennai-to-pondicherry-road-trip",
    title: "Chennai to Pondicherry Road Trip Guide",
    excerpt: "Plan the perfect Chennai to Pondicherry road trip via ECR — route guide, pit stops at Mahabalipuram, Alamparai Fort, travel tips, and why booking a taxi beats self-drive.",
    date: "March 5, 2026",
    readTime: "9 min read",
    content: `The Chennai to Pondicherry road trip via the East Coast Road (ECR) is one of South India's most scenic drives. Covering approximately 150 kilometers along the stunning Bay of Bengal coastline, this 3-hour journey is filled with amazing pit stops, historical sites, and breathtaking ocean views. Here's your complete guide to making the most of this iconic route.

**The Route: East Coast Road (ECR)**
The East Coast Road, officially known as State Highway 49, runs parallel to the Bay of Bengal from Chennai to Cuddalore, passing through Pondicherry. Starting from Thiruvanmiyur in Chennai, the road hugs the coastline, offering intermittent glimpses of the sea through coconut groves and casuarina plantations.

**Must-Stop Attractions Along the Way**

1. **Muttukadu (25 km from Chennai)**: Home to the Madras Crocodile Bank and an TTDC boating facility. Perfect for a quick stop to stretch your legs and see some wildlife.

2. **Mahabalipuram (58 km)**: This UNESCO World Heritage Site is the crown jewel of the ECR. The Shore Temple, Five Rathas, and Arjuna's Penance are must-see ancient monuments. Plan to spend at least 2 hours here. The beachside seafood restaurants are excellent for lunch.

3. **Alamparai Fort (75 km)**: A lesser-known gem, this 17th-century Mughal fort sits dramatically on the coastline. The ruins against the backdrop of the sea make for stunning photographs.

4. **Pondicherry (150 km)**: Your destination! Arrive refreshed and ready to explore the French Quarter, beaches, and cafés.

**Best Time for the Road Trip**
Early morning (5-6 AM departure from Chennai) is ideal. You'll enjoy empty roads, beautiful sunrise views over the ocean, and arrive in Pondicherry by breakfast time. Weekend evenings on ECR can be heavily congested.

**Road Trip Tips**
- The ECR has several speed cameras — maintain speed limits
- Fuel stations are available every 20-30 km
- Carry cash as some smaller stops don't accept cards
- The road is well-maintained but watch for speed breakers near villages

**Why Book a Taxi Instead of Self-Drive?**
While self-driving is an option, booking a Pondicherry Taxi for your ECR road trip has significant advantages. You can enjoy the scenery without worrying about navigation. Your driver knows the best pit stops and can recommend hidden gems. You arrive relaxed, not tired from driving. And you don't need to worry about parking in Pondicherry's narrow streets.

Book your Chennai to Pondicherry taxi today and let us handle the driving while you soak in the views! Call +91 99524 93907 for instant booking.`,
  },
];

const BlogList = () => (
  <Layout>
    <SEOHead
      title="Pondicherry Travel Blog 2026 — Places to Visit, Road Trip Guides & Sightseeing Taxi Tips"
      description="Explore Pondicherry travel guides, best places to visit, Chennai to Pondicherry road trip via ECR, and sightseeing taxi tips. Plan your perfect Puducherry trip — updated 2026."
      keywords="Pondicherry travel blog, best places to visit in Pondicherry, Pondicherry travel guide 2026, Chennai to Pondicherry road trip, Chennai to Pondicherry taxi, Pondicherry sightseeing taxi, Pondicherry local sightseeing taxi, Pondicherry city tour packages, taxi services in Pondicherry, best taxi services in Pondicherry, affordable taxi fares Pondicherry, Pondicherry airport taxi service, airport pickup and drop Pondicherry, Pondicherry to Chennai cab, outstation taxi Pondicherry, one-way drop taxi Pondicherry, tours and travels in Pondicherry, 24/7 taxi services Pondicherry, corporate cab service Pondicherry,Pondicherry Travel Tips, Pondicherry Itinerary, Things to Do in Pondicherry, Weekend Trip to Pondicherry, Pondicherry Tourist Attractions"
      canonical="https://pondicherry.taxi/blog"
    />

    <section className="pt-28 pb-16 section-padding bg-gradient-to-b from-primary/5 to-transparent">
      <div className="container-custom text-center">
        <motion.h1 {...fadeUp} className="text-4xl sm:text-5xl font-bold">
          <span className="text-gradient">Travel Blog</span>
        </motion.h1>
        <motion.p {...fadeUp} className="mt-4 text-muted-foreground max-w-2xl mx-auto">
          Expert guides on the best places to visit in Pondicherry, Chennai to Pondicherry road trips, sightseeing taxi tips, and everything Puducherry.
        </motion.p>
      </div>
    </section>

    <section className="section-padding">
      <div className="container-custom">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((post, i) => (
            <motion.article key={post.slug} {...fadeUp} transition={{ delay: i * 0.1 }}>
              <Link to={`/blog/${post.slug}`} className="glass-card-hover rounded-2xl overflow-hidden block group h-full">
                <div className="h-48 bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                  <Calendar className="w-16 h-16 text-primary/30" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {post.date}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {post.readTime}</span>
                  </div>
                  <h2 className="text-lg font-bold text-foreground group-hover:text-primary transition-colors mb-2">
                    {post.title}
                  </h2>
                  <p className="text-sm text-muted-foreground line-clamp-3">{post.excerpt}</p>
                  <span className="mt-4 inline-flex items-center text-sm text-primary font-medium">
                    Read More <ArrowRight className="w-4 h-4 ml-1 group-hover:ml-2 transition-all" />
                  </span>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  </Layout>
);

export const blogPosts = posts;
export default BlogList;
